import { onMounted } from 'vue'
import { isAxiosError } from 'axios'
import {
  addRumAction,
  addRumError,
  cleanContext,
  HTTP_UNPROCESSABLE_ENTITY_CODE,
  isDatadogRumInitialized,
  RUM_SKIP_CLIENT_ERROR_STATUS_CODES,
} from '@/Utils/DatadogRum.js'

/**
 * Flag to add on the axios request config to skip the error tracking interceptor in BaseApi
 */
export const RUM_SKIP_INTERCEPTOR_ERROR_TRACKING = 'RUM_SKIP_INTERCEPTOR_ERROR_TRACKING'

/**
 * Composable to track custom actions in Datadog RUM.
 *
 * @param {string} actionName - The name of the custom action to track
 * @param {Record<string, unknown>} context - The context of the custom action to track (no PII)
 */
export function useRumCustomAction(actionName, context = {}) {
  if (!isDatadogRumInitialized()) {
    return
  }

  onMounted(() => {
    addRumAction(actionName, cleanContext(context))
  })
}

/**
 * @param {import('axios').AxiosError} axiosError - The axios error
 *
 * @returns {Record<string, unknown>} The context of the error
 */
function parseContextFromAxiosError(axiosError) {
  const { message, response } = axiosError
  const status = parseInt(response?.status ?? 0, 10)
  const body = response?.data ?? {}
  const context = {
    message: body?.message ?? message,
    method: response?.config?.method?.toUpperCase(),
    uri: getUrlPathGroup(response?.config?.url),
    status: status,
  }

  if (status === HTTP_UNPROCESSABLE_ENTITY_CODE) {
    context.fields = body?.data ?? {}
  }

  return context
}

/**
 * Parse the context from the response error
 *
 * @param {Object} responseError - The response error
 * @returns {Record<string, unknown>} The context of the error
 */
function parseContextFromResponseError(responseError) {
  switch (true) {
    case isAxiosError(responseError):
      return parseContextFromAxiosError(responseError)
    default:
      return {
        message: responseError?.message ?? 'Unknown error',
        status: parseInt(responseError?.status ?? 0, 10),
      }
  }
}

/**
 * Add a RUM error to the Datadog RUM session
 *
 * @param {Object} responseError - The response error
 * @param {Record<string, unknown>} context - The context of the error
 */
export function addRumResponseError(
  responseError,
  context = {},
  skipErrorStatuses = RUM_SKIP_CLIENT_ERROR_STATUS_CODES
) {
  try {
    if (!isDatadogRumInitialized()) {
      return
    }

    const { message, ...restContext } = parseContextFromResponseError(responseError)

    if (skipErrorStatuses.includes(restContext.status)) {
      return
    }

    const error = new Error(message)

    addRumError(error, {
      ...restContext,
      ...context,
    })
  } catch (error) {
    console.error('Error adding RUM Response error', error)
  }
}

/**
 * Get the URL path group generated for similar URLs. For example, dashboard/1234 and /dashboard/9a become /dashboard/?.
 * A path segment that contains at least one number is considered a variable ID.
 *
 * @param {string} url
 * @returns {string}
 */
export function getUrlPathGroup(url) {
  if (!url || typeof url !== 'string') {
    return '/'
  }

  let pathname = url.trim()

  try {
    pathname = new URL(pathname).pathname
  } catch {
    pathname = pathname.split(/[?#]/)[0]
  }

  const trimmed = pathname.replace(/\/+$/, '') || '/'

  if (trimmed === '/') {
    return '/'
  }

  const segments = trimmed.slice(1).split('/')
  const apiVersion = segments.shift()
  const grouped = segments.map((segment) => (/[0-9]/.test(segment) ? '?' : segment))

  return `/${apiVersion}/${grouped.join('/')}`
}
